import { _timeZones } from "global/common/dateFormatting";
import { manifestSizes } from "global/components/imaging/createManifestIcons";
import { appFormTypes, appUserFormTypes } from "global/enums/appFormTypes";
import { gEnums, listTypes } from "global/enums/globalEnums";
import { listHelpers, listList } from "global/lists/lists";
import _ from "lodash";
import { _projectAppNames, _projectAppNamesNonDev } from "project/appConfiguration";
import { useContext, useMemo } from "react";
import { ParentContext } from "../../ParentContext";
import { useAppUserAccess } from "../useAppUserAccess";

export const useOptionsList = (props = {}) => {

  const { listType, viewProps, dataCollectionName } = props;

  const vpKeys = viewProps ? _.map(viewProps, (viewProp) => viewProp?.data.listType) : []

  return useMemo(() => {
    const ol = {};

    // 🔹 Base lists
    _.forEach(vpKeys, (vpKey) => {
      switch (vpKey) {
        case listTypes.manifestSizes:
          ol[listTypes.manifestSizes] = listList(manifestSizes);
          break;
        case listTypes.settingGroupTypes:
          ol[listTypes.settingGroupTypes] = listList(gEnums.settingsMenuGroupTypes, true);
          break;
        case listTypes.roundTypes:
          ol[listTypes.roundTypes] = listList(gEnums.roundTypes, true);
          break;
        case listTypes.timeZones:
          ol[listTypes.timeZones] = listList(_timeZones, true);
          break;
      }
    })

    // 🔹 ViewProp-driven dynamic lists
    if (viewProps) {
      _.forEach(viewProps, (viewProp) => {
        const data_prop = viewProp?.data;
        const { listType: propListType, labelProps } = data_prop ?? {};

        // Add lists directly referenced in the prop
        if (propListType && gEnums[propListType]) {
          ol[propListType] = createItemList(gEnums[propListType]);
        }

        // Add lists referenced inside labelProps
        _.forEach(labelProps, (lpr) => {
          const label_listType = lpr?.data?.listType;
          if (label_listType && gEnums[label_listType]) {
            ol[label_listType] = createItemList(gEnums[label_listType]);
          }
        });
      });
    }
    // 🔹 Otherwise, populate all gEnums lists
    else {
      _.forEach(gEnums, (value, key) => {
        ol[key] = createItemList(value);
      });
    }

    // 🔹 Return specific or full list object
    return listType ? ol[listType] : ol;
  }, []);
  // }, [listType, viewProps]);
};

export const useListTypeOptions = ({ viewProps, dataCollectionName }) => {

  const appUserAccess = useAppUserAccess()
  const { isDeveloper } = appUserAccess ?? {}

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { currentSettings_state } = states ?? {}
  const { aps_global, aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});

  const ols = useMemo(() => {
    const _ol = {}
    _.forEach(viewProps, (viewProp) => {
      const { data } = viewProp ?? {}
      const { listType } = data ?? {};
      switch (listType) {
        case listTypes.dataList:
          _ol[listType] = listHelpers.listList(listTypes)
          break;
        case listTypes.manifestSizes:
          _ol[listType] = listHelpers.listList(manifestSizes)
          break;
        case listTypes.timeZones:
          _ol[listType] = listHelpers.listList(_timeZones)
          break;
        case listTypes.appFormTypes:
          _ol[listType] = listHelpers.listList(appFormTypes)
          break;
        case listTypes.appUserFormTypes:
          _ol[listType] = listHelpers.listList(appUserFormTypes)
          break;
        case listTypes.appNames:
          _ol[listType] = listHelpers.listList(isDeveloper ? _projectAppNames : _projectAppNamesNonDev)
          break;
        case listTypes.dataFilters:
          const cvi = aps_viewItems && dataCollectionName ? aps_viewItems[dataCollectionName] : null
          if (cvi) {
            const { dataFilters } = cvi ?? {}
            if (dataFilters) {
              _ol[listType] = listHelpers.listListO(dataFilters)
            }
          }
          break;
        case listTypes.notificationDeliveryTypes:
          const { appNotifications } = aps_global ?? {}
          const { allowPushNotifications, allowSmsNotifications, allowEmailNotifications } = appNotifications ?? {}
          if (listTypes[listType] && gEnums[listType]) {
            const ndtList = gEnums[listType]
            if (!allowPushNotifications) { delete ndtList.push }
            if (!allowSmsNotifications) { delete ndtList.sms }
            if (!allowEmailNotifications) { delete ndtList.email }
            _ol[listType] = createItemList(ndtList)
          }
          break;
        default:
          if (listTypes[listType] && gEnums[listType]) {
            _ol[listType] = createItemList(gEnums[listType])
          }
          break;
      }
    })
    return _ol
  }, [])
  //   }, [viewProps])
  return ols
}


export const createItemList = (list) => {
  if (_.isEmpty(list)) return [];

  // 🔹 If it's an array: simple mapping
  if (_.isArray(list)) {
    return _.map(list, (key) => ({
      text: _.startCase(key),
      value: key,
      key,
    }));
  }

  // 🔹 If it's an object: map over its entries
  return _.map(list, (val, key) => {
    const hasCaption = _.has(val, "caption");
    return {
      text: hasCaption ? val.caption : _.startCase(key),
      value: hasCaption ? val.value ?? key : key,
      key: hasCaption ? val.key ?? key : key,
    };
  });
};

export const createGroupList = (list, childMenuItem) => {
  if (_.isEmpty(list)) return [];
  return _.map(list, (listItem) => {
    const { text, value, key } = listItem
    return {
      key,
      caption: text,
      childMenuItem,
    };
  });
}